// Three browser primitives that everything else in dashboard-web composes:
//
//   1. getLatest(subject)         — single point lookup via $JS.API.DIRECT.GET
//   2. subscribeStream(opts)      — 8h replay then live, AsyncIterable + stop()
//   3. getStreamInfo()            — clamp + lake-age banner data
//
// No raw nats.ws calls live anywhere else under dashboard-web/src/nats/. A
// future transport swap (gRPC-Web, BFF SSE, whatever) is a one-file change.

import { DeliverPolicy, type StoredMsg } from 'nats.ws';
import { getConnection } from './jsClient';
import { fromJsMsg, type JsEnvelope } from './envelope';
import { parseSubject } from './subject';

const STREAM_NAME = 'SENSOR_DATA';

/** Convert ms to nanoseconds for nats.ws fields that take Nanos. */
const nanos = (ms: number): number => ms * 1_000_000;

// ── 1. getLatest ──────────────────────────────────────────────────────────

/**
 * Fetch the most recent message for a subject via `$JS.API.DIRECT.GET`.
 * Requires the browser JWT to grant `Pub.Allow $JS.API.DIRECT.GET.>`
 * (server-side change tracked in plan v2 §10).
 *
 * Returns null when no message is stored for the subject (broker 404).
 */
export async function getLatest(subject: string): Promise<JsEnvelope | null> {
  const nc = await getConnection();
  const js = nc.jetstream();
  const stream = await js.streams.get(STREAM_NAME);
  try {
    const sm = await stream.getMessage({ last_by_subj: subject });
    if (!sm) return null;
    return fromStoredMsg(sm);
  } catch (err) {
    if (isNotFound(err)) return null;
    throw err;
  }
}

function isNotFound(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const code = (err as { code?: string | number }).code;
  return code === '404' || code === 404 || code === '10037' || /no message/i.test(String(err));
}

function fromStoredMsg(sm: StoredMsg): JsEnvelope | null {
  // StoredMsg lacks DeliveryInfo (it's a stream-API result, not a consumer
  // delivery). Synthesize the JsMsg shape `fromJsMsg` expects.
  const parsed = parseSubject(sm.subject);
  if (!parsed) return null;
  const synthetic = {
    subject: sm.subject,
    data: sm.data,
    headers: sm.header,
    seq: sm.seq,
    info: {
      streamSequence: sm.seq,
      timestampNanos: nanos(sm.time.getTime()),
    },
  } as unknown as Parameters<typeof fromJsMsg>[0];
  return fromJsMsg(synthetic);
}

// ── 2. subscribeStream ────────────────────────────────────────────────────

export interface SubscribeOpts {
  /** e.g. [`${tenant}.>`]. Server-side JWT scope already enforces tenancy. */
  filterSubjects: string[];
  /** Default: now - 8h. Ignored if startSeq is set. */
  startMs?: number;
  /** Warm-resume from the last seen stream sequence + 1. Takes precedence over startMs. */
  startSeq?: number;
  /** Caller-owned cancel signal; the iterator stops when it fires. */
  signal?: AbortSignal;
}

export interface SubscribeHandle {
  messages: AsyncIterable<JsEnvelope>;
  stop: () => Promise<void>;
}

const EIGHT_HOURS_MS = 8 * 60 * 60 * 1000;

export async function subscribeStream(opts: SubscribeOpts): Promise<SubscribeHandle> {
  const nc = await getConnection();
  const js = nc.jetstream();

  // Two delivery modes — start-by-seq for warm resume, start-by-time for cold.
  const startCfg = opts.startSeq != null
    ? {
        deliver_policy: DeliverPolicy.StartSequence as const,
        opt_start_seq:  opts.startSeq,
      }
    : {
        deliver_policy: DeliverPolicy.StartTime as const,
        opt_start_time: new Date(opts.startMs ?? (Date.now() - EIGHT_HOURS_MS)).toISOString(),
      };

  // Note on field names: the ordered-consumer config in nats.ws v1.30 uses
  // `filterSubjects` (camelCase) but `deliver_policy` / `opt_start_*` /
  // `inactive_threshold` (snake). See node_modules/nats.ws/lib/jetstream/
  // consumer.d.ts:270 (OrderedConsumerOptions).
  const consumer = await js.consumers.get(STREAM_NAME, {
    filterSubjects: opts.filterSubjects,
    // 15s — backstop for when the client dies before issuing a clean
    // consumer.delete (tab close, network drop, crash). Was 30s but
    // we kept seeing orphans in the NATS console; the shorter window
    // is fine because the watermark + IDB cache handles warm-resume
    // without needing the server-side consumer to survive.
    inactive_threshold: nanos(15_000),
    ...startCfg,
  });

  const iter = await consumer.consume();
  let stopped = false;

  const messages: AsyncIterable<JsEnvelope> = {
    [Symbol.asyncIterator]: async function* () {
      try {
        for await (const m of iter) {
          if (stopped || opts.signal?.aborted) {
            try { iter.stop(); } catch { /* */ }
            break;
          }
          const env = fromJsMsg(m);
          if (env) yield env;
        }
      } catch (err) {
        if (!stopped) throw err;
      }
    },
  };

  const stop = async () => {
    stopped = true;
    try { iter.stop(); } catch { /* */ }
    // Explicit server-side delete. Ordered consumers do have
    // `inactive_threshold: 30s`, but Chrome throttles background-tab
    // timers to ~1 Hz, which is enough for nats.ws's consume() heartbeat
    // to keep the consumer "alive" indefinitely from the server's POV.
    // Result: every backgrounded tab leaks a consumer that holds every
    // matching message in its pending queue. Explicit .delete() on
    // teardown closes that gap; beforeunload (wired in useJsConsumer)
    // covers the tab-close case where React cleanup doesn't fire.
    try { await consumer.delete(); } catch { /* */ }
  };

  if (opts.signal) {
    opts.signal.addEventListener('abort', () => { void stop(); }, { once: true });
  }

  return { messages, stop };
}

// ── 3. getStreamInfo ──────────────────────────────────────────────────────

export interface StreamInfoSnapshot {
  /** Stream MaxAge in ms. ~28 800 000 once agent2 phase 1 is live. */
  maxAgeMs: number;
  /** Sequence + timestamp of the oldest stored message. */
  firstSeq: number;
  firstTimeMs: number;
  /** Sequence of the newest stored message. */
  lastSeq: number;
  /** Duplicates window in ms. ~600 000 once agent2 phase 1 is live. */
  duplicatesWindowMs: number;
}

export async function getStreamInfo(): Promise<StreamInfoSnapshot> {
  const nc = await getConnection();
  // Skip the account-info probe to $JS.API.INFO — browser creds
  // (MintBrowserCreds) only grant the scoped $JS.API.STREAM.INFO.>, so the
  // default probe trips a PERMISSIONS_VIOLATION. Stream info reads fine via
  // the allowed scoped subject. The installed nats.ws runtime takes a bare
  // `checkAPI` boolean here (esm build) though its .d.ts types an options
  // object — cast so the runtime gets the boolean it actually reads.
  const jsm = await nc.jetstreamManager(false as never);
  const info = await jsm.streams.info(STREAM_NAME);
  return {
    maxAgeMs:           Math.floor(Number(info.config.max_age) / 1_000_000),
    firstSeq:           info.state.first_seq,
    firstTimeMs:        Date.parse(info.state.first_ts),
    lastSeq:            info.state.last_seq,
    duplicatesWindowMs: Math.floor(Number(info.config.duplicate_window) / 1_000_000),
  };
}
